"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, CalendarCheck, Clock, ShieldCheck, Zap, TriangleAlert as AlertTriangle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const keyHighlights = [{
  icon: Clock,
  title: "45-Minute Response",
  description: "Rapid mobile dispatch throughout Jonesboro and Clayton County."
}, {
  icon: ShieldCheck,
  title: "Licensed & Insured",
  description: "Certified master plumbers with clear, upfront estimates."
}, {
  icon: Zap,
  title: "24/7 Live Support",
  description: "No answering machine delays when pipes burst or drains back up."
}];
export default function EmergencyCallToAction() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="emergencycalltoaction" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="relative overflow-hidden rounded-2xl bg-primary text-primary-foreground shadow-2xl p-8 sm:p-12 lg:p-16">
          {/* Subtle decorative background glow */}
          <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 size-96 rounded-full bg-accent/20 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-24 size-96 rounded-full bg-secondary/15 blur-3xl" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content Area */}
            <div className="lg:col-span-7 space-y-6">
              <motion.div variants={itemVariants} className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-accent text-accent-foreground font-semibold px-3 py-1 flex items-center gap-1.5">
                  <AlertTriangle className="size-3.5 text-accent-foreground" />
                  Plumbing Crisis in Jonesboro?
                </Badge>
                <span className="text-xs sm:text-sm font-medium tracking-wide uppercase text-primary-foreground/80">
                  Ready Right Now
                </span>
              </motion.div>

              <motion.h2 variants={itemVariants} className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-tight">
                Immediate Emergency Plumbing Repair in Minutes
              </motion.h2>

              <motion.p variants={itemVariants} className="text-base sm:text-lg text-primary-foreground/90 max-w-2xl leading-relaxed">
                Burst pipe, leaking water heater, or severe sewer backup? Don&apos;t wait for water damage to spread. Our local Jonesboro emergency crew is standing by with fully stocked trucks ready to solve your problem on the spot.
              </motion.p>

              {/* Action Buttons */}
              <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base h-12 px-6 shadow-md">
                  <a href="tel:4708479076" className="flex items-center justify-center gap-2.5">
                    <PhoneCall className="size-5" />
                    <span>Call (470) 847-9076 Now</span>
                  </a>
                </Button>

                <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 font-medium text-base h-12 px-6">
                  <Link to="#sewer-service-request-form" className="flex items-center justify-center gap-2">
                    <CalendarCheck className="size-5" />
                    <span>Request Online Service</span>
                  </Link>
                </Button>
              </motion.div>
            </div>

            {/* Right Highlights Grid */}
            <div className="lg:col-span-5 flex flex-col gap-4">
              {keyHighlights.map((highlight, index) => {
              const IconComponent = highlight.icon;
              return <motion.div key={highlight.title} data-index={index} variants={itemVariants}>
                    <Card className="bg-primary-foreground/10 border-primary-foreground/15 text-primary-foreground shadow-sm backdrop-blur-xs py-4">
                      <CardContent className="px-5 py-0 flex items-start gap-4">
                        <div className="rounded-lg bg-accent text-accent-foreground p-2.5 shrink-0 mt-0.5">
                          <IconComponent className="size-5" />
                        </div>
                        <div className="space-y-1">
                          <h3 className="font-default font-semibold text-base text-primary-foreground">
                            {highlight.title}
                          </h3>
                          <p className="text-sm text-primary-foreground/80 leading-snug">
                            {highlight.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>;
            })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}